import { useEffect, useState } from 'react';

import { apiClient } from '@/api/client';

import type { Role } from './useRoles';

export type MeResponse = {
  oid: string;
  name: string | null;
  email: string | null;
  preferred_username: string | null;
  roles: Role[];
  /**
   * Advisor profile, sourced from GAMX advisor-info at ISAM login (see
   * GamxAdvisorDetailClient) and surfaced as claims on the BFF session.
   * Absent for Entra (originator / approver) sessions and for advisors whose
   * profile lookup failed — the runtime prefill treats missing as "no value"
   * rather than blanking a field the advisor may have typed into.
   */
  phoneNumber?: string | null;
  dealerCode?: string | null;
  representativeCodes?: string[] | null;
  /** Unix seconds — when the BFF's stored IdP access token expires. Informational. */
  accessTokenExpiresAt: number;
  /** Unix seconds — the absolute session cap (fixed from login; activity can't extend it). */
  sessionExpiresAt: number;
  /** Server-driven cadence, in seconds, for the session timer's refresh interval. */
  refreshIntervalSeconds: number;
};

// Module-level request deduplication: one in-flight fetch shared across all callers.
// Cleared on logout via invalidateSession().
let _promise: Promise<MeResponse | null> | null = null;
let _cached: MeResponse | null | undefined = undefined;
/**
 * When the last /me attempt FAILED, not when it resolved to "no session".
 *
 * A failure used to be cached like an answer (`_cached = null`), which the
 * early return then treated as final for the lifetime of the page. One blip —
 * an API redeploy, a dropped connection, a cold Lambda — permanently and
 * SILENTLY disabled everything that reads the session: advisor-profile
 * prefill, role checks, the session timer. Nothing surfaced an error; fields
 * simply stopped populating, which reads as "the feature is broken".
 *
 * So a failure is now retryable. The cooldown keeps a genuinely dead endpoint
 * from turning every mount into a request — FieldRenderer calls useSession per
 * field, so an unbounded retry would be a storm.
 */
let _failedAt: number | null = null;
const FAILURE_RETRY_MS = 5_000;

function fetchSessionOnce(): Promise<MeResponse | null> {
  // A real answer (including a legitimate "no session" null from a 401 that
  // resolved) is cached; only transport failures are retried.
  if (_cached !== undefined && _cached !== null) return Promise.resolve(_cached);
  if (_cached === null && _failedAt !== null && Date.now() - _failedAt < FAILURE_RETRY_MS) {
    return Promise.resolve(null);
  }
  if (_promise) return _promise;
  _promise = apiClient
    .get<MeResponse>('/me')
    .then((res) => {
      _cached = res.data;
      _failedAt = null;
      _promise = null;
      return _cached;
    })
    .catch(() => {
      _cached = null;
      _failedAt = Date.now();
      _promise = null;
      return null;
    });
  return _promise;
}

/** Test seam — the retry policy above is the whole point of the module, and
 *  it isn't reachable through the hook without a DOM. Not for app code. */
export const useSessionInternals = { fetchSessionOnce, FAILURE_RETRY_MS };

/** Call after logout to force a fresh /me fetch on the next navigation. */
export function invalidateSession(): void {
  _cached = undefined;
  _promise = null;
  _failedAt = null;
}

/**
 * Resolves the current server-side session by calling GET /api/v1/me.
 * Multiple simultaneous callers share one in-flight request; the result is cached
 * for the lifetime of the page (invalidated on logout).
 */
export function useSession(): { user: MeResponse | null; loading: boolean } {
  const [user, setUser] = useState<MeResponse | null>(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined);

  useEffect(() => {
    // Only short-circuit on a REAL cached session. A cached null may be a
    // past failure, and fetchSessionOnce decides whether that is retryable —
    // short-circuiting here would reinstate the poisoned-cache bug one layer
    // up, since a mount after the cooldown would never re-ask.
    if (_cached !== undefined && _cached !== null) {
      setUser(_cached);
      setLoading(false);
      return;
    }
    let alive = true;
    void fetchSessionOnce().then((data) => {
      if (!alive) return;
      setUser(data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { user, loading };
}
